import {
    EventSource,
    type EventSourceConnectParams,
    type EventSourceError,
    type EventsSourceConfig,
} from '../event-source';
import { noop } from '../utils';
import { MercureObserver } from './MercureObserver';
import { getMercureMessageData } from './getMercureMessageData';
import type { MercureEventListener, MercureEventUnsubscribe, MercureMessage } from './types';

export class MercureEventSource {
    private requestParams: MercureRequestParams;
    private es: EventSource | undefined;
    private options: MercureEventSourceParams | undefined;
    private observer: MercureObserver;

    public onMessageError: (error: MercureEventSourceError) => void;
    public onConnectionError: (error: EventSourceError) => void;

    constructor(requestParams: MercureRequestParams, options?: MercureEventSourceParams) {
        this.requestParams = { ...requestParams };
        this.options = options;
        this.observer = new MercureObserver();
        this.onMessageError = noop;
        this.onConnectionError = noop;
    }

    public addListener(
        topic: string,
        types: string[],
        listener: MercureEventListener,
    ): MercureEventUnsubscribe {
        const isNewTopic = this.observer.hasTopic(topic) === false;

        types.forEach((type) => this.observer.addListener(topic, type, listener));

        const shouldReconnect = !this.hasConnection() || isNewTopic;
        if (shouldReconnect) {
            this.reconnect();
        }

        return () => {
            types.forEach((type) => this.observer.removeListener(topic, type, listener));

            const shouldDisconnect = this.observer.getTopics().length === 0;
            if (shouldDisconnect) {
                this.disconnect();

                return;
            }

            const shouldRemoveTopic = !this.observer.hasTopic(topic);
            if (shouldRemoveTopic) {
                this.reconnect();

                return;
            }
        };
    }

    private connect() {
        this.es = new EventSource(this.options);
        this.es.onMessage = (...args) => this.handleMessage(...args);
        this.es.onConnectionError = (...args) => this.handleConnectionError(...args);

        const url = this.deriveMercureUrl(this.observer.getTopics());
        void this.es.connect({ ...this.requestParams, url });
    }

    private disconnect() {
        if (this.es) {
            this.es.disconnect();
            this.es = undefined;
        }
    }

    private reconnect() {
        this.disconnect();
        this.connect();
    }

    cancel() {
        this.disconnect();
        this.observer.clean();
    }

    hasConnection() {
        return this.es?.hasConnection() ?? false;
    }

    private handleMessage(msg: MercureMessage) {
        try {
            const data = getMercureMessageData(msg);

            const type: string = data.type;
            if (!type) {
                throw new Error(`Missing \`type\` in mercure message`);
            }

            this.observer.emitEvent(type, msg);
        } catch (error) {
            const exposedError = new MercureEventSourceError({
                cause: error,
                msg: { event: msg.event },
            });

            this.onMessageError(exposedError);
        }
    }

    private handleConnectionError(error: EventSourceError) {
        this.onConnectionError(error);
    }

    private deriveMercureUrl(topics: string[]) {
        const hubUrl = new URL(this.requestParams.url);
        topics.forEach((topic) => hubUrl.searchParams.append('topic', topic));

        return hubUrl.toString();
    }
}

export class MercureEventSourceError extends Error {
    cause: unknown;
    msg?: Partial<MercureMessage>;

    constructor({ cause, msg }: { cause: unknown; msg: Partial<MercureMessage> }) {
        const message = cause instanceof Error ? cause.message : 'Unknown error';
        super(message);
        this.name = 'MercureEventSourceError';
        this.cause = cause;
        this.msg = msg;
    }
}

export type MercureRequestParams = EventSourceConnectParams;
export type MercureEventSourceParams = EventsSourceConfig;
